/**
 * A JoinApplication tracks a member's request or invitation to join an online program, from application through approval, payment, and enrollment.
 *
 * Site owners can invite members, review applications, and manage payment-related activity. Members can apply to join a program, accept invitations, and complete payment when required.
 */
export interface JoinApplication {
  /**
   * JoinApplication ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.
   *
   * This API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the join application was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the join application was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Member ID of the site member applying or invited to participate in the program.
   *
   * Required when creating applications as a site owner with `manage_applications` permission.
   * Automatically set to the caller's member ID when members create their own applications.
   * @format GUID
   * @readonly
   */
  memberId?: string | null;
  /**
   * Program ID which this join application is related to.
   * @format GUID
   * @immutable
   */
  programId?: string;
  /**
   * Current status of the join application.
   *
   * Read-only for member-initiated applications.
   * Site owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Payment status of join application.
   * @readonly
   */
  paymentStatus?: PaymentStatusWithLiterals;
  /**
   * Payment info - represents associated Wix Payments or Pricing Plans data.
   * @readonly
   */
  paymentInfo?: PaymentInfo;
  /**
   * Read-only snapshot of member information from the Members API for `member_id`.
   * This is not the authoritative member record.
   * @readonly
   */
  member?: Member;
  /**
   * Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).
   * @format GUID
   * @readonly
   */
  participantId?: string | null;
  /**
   * Custom field data for the JoinApplication.
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)
   * must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Tags for categorizing and organizing join applications. */
  tags?: Tags;
}

export enum Status {
  /** Member has been invited to join the program and has not yet accepted. */
  INVITED = 'INVITED',
  /** Member's application is awaiting the site owner's decision. */
  APPROVAL_PENDING = 'APPROVAL_PENDING',
  /** Application is approved. Payment may still be required before the member is enrolled. */
  APPROVED = 'APPROVED',
  /** Member canceled the application. The service removes the join application asynchronously. */
  CANCELLED = 'CANCELLED',
  /** Site owner declined the application. The service removes the join application asynchronously. */
  DECLINED = 'DECLINED',
  /** Connected participant is suspended after the related Pricing Plans subscription expires. A successful new payment resumes the participation flow. */
  SUSPENDED = 'SUSPENDED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'INVITED'
  | 'APPROVAL_PENDING'
  | 'APPROVED'
  | 'CANCELLED'
  | 'DECLINED'
  | 'SUSPENDED';

export enum PaymentStatus {
  /** Payment isn't required because the program is free or the member has an eligible Pricing Plans entitlement. */
  NO_PAYMENT_REQUIRED = 'NO_PAYMENT_REQUIRED',
  /** Payment is required before the member can be enrolled. */
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  /** Payment checkout or a Pricing Plans purchase is in progress. */
  PAYMENT_IN_PROGRESS = 'PAYMENT_IN_PROGRESS',
  /** Member submitted an offline payment that awaits the site owner's decision. */
  PENDING_OWNER_APPROVAL = 'PENDING_OWNER_APPROVAL',
  /** Payment completed successfully. */
  PAYMENT_SUCCESSFUL = 'PAYMENT_SUCCESSFUL',
  /** Payment was declined by the payment system or, for an offline payment, by the site owner. */
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  /** Member canceled payment. */
  PAYMENT_CANCELED = 'PAYMENT_CANCELED',
}

/** @enumType */
export type PaymentStatusWithLiterals =
  | PaymentStatus
  | 'NO_PAYMENT_REQUIRED'
  | 'PAYMENT_PENDING'
  | 'PAYMENT_IN_PROGRESS'
  | 'PENDING_OWNER_APPROVAL'
  | 'PAYMENT_SUCCESSFUL'
  | 'PAYMENT_FAILED'
  | 'PAYMENT_CANCELED';

export interface PaymentInfo {
  /**
   * One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.
   * Wix Payments order id, populated when UoU selects One-time payment at the checkout
   * @format GUID
   * @readonly
   */
  paymentOrderId?: string | null;
  /**
   * Offline transaction id, populated when UoU selects manual payment type at the checkout
   * @format GUID
   * @readonly
   */
  offlineTransactionId?: string | null;
  /**
   * Pricing Plans IDs connected to the program, populated when UoU selects Pricing Plans at the checkout
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  paidPlanIds?: string[];
  /**
   * Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon
   * @format GUID
   * @readonly
   */
  couponId?: string | null;
}

export interface Member {
  /**
   * Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.
   * Member contact's first name
   * @readonly
   * @maxLength 1000
   */
  firstName?: string | null;
  /**
   * Member contact's last name
   * @readonly
   * @maxLength 1000
   */
  lastName?: string | null;
  /**
   * Member login email
   * @readonly
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Member contact id
   * @format GUID
   * @readonly
   */
  contactId?: string | null;
  /**
   * Member nickname
   * @readonly
   * @maxLength 1000
   */
  nickname?: string | null;
  /**
   * Member profile image url
   * @readonly
   * @format WEB_URL
   */
  profileImageUrl?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, typically restricted from site members and visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone with access to the entity, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

/**
 * Message for reindexing search data to a given search schema. Support both upsert and delete flows as well as
 * performs context manipulation with adding tenant, provided in message to callscope.
 */
export interface ReindexMessage extends ReindexMessageActionOneOf {
  upsert?: Upsert;
  delete?: Delete;
  entityFqdn?: string;
  tenantId?: string;
  eventTime?: Date | null;
  entityEventSequence?: string | null;
  schema?: Schema;
}

/** @oneof */
export interface ReindexMessageActionOneOf {
  upsert?: Upsert;
  delete?: Delete;
}

export interface Upsert {
  entityId?: string;
  entityAsJson?: string;
}

export interface Delete {
  entityId?: string;
}

export interface Schema {
  label?: string;
  clusterName?: string;
}

/** Domain event emitted when tags are modified */
export interface JoinApplicationTagsModified {
  /** The JoinApplication with updated tags */
  joinApplication?: JoinApplication;
  /** Tags that were assigned */
  assignedTags?: Tags;
  /** Tags that were unassigned */
  unassignedTags?: Tags;
}

/** Domain event emitted when status is modified */
export interface StatusChanged {
  /** The JoinApplication with updated status */
  joinApplication?: JoinApplication;
  /** Previous status */
  previousStatus?: StatusWithLiterals;
}

/** Domain event emitted when payment status is modified */
export interface PaymentStatusChanged {
  /** The JoinApplication with updated payment status */
  joinApplication?: JoinApplication;
  /** Previous payment status */
  previousPaymentStatus?: PaymentStatusWithLiterals;
}

export interface CreateJoinApplicationRequest {
  /** JoinApplication to be created. */
  joinApplication: JoinApplication;
}

export interface CreateJoinApplicationResponse {
  /** The created JoinApplication. */
  joinApplication?: JoinApplication;
}

export interface GetJoinApplicationRequest {
  /**
   * ID of the JoinApplication to retrieve.
   * @format GUID
   */
  joinApplicationId: string;
}

export interface GetJoinApplicationResponse {
  /** The requested JoinApplication. */
  joinApplication?: JoinApplication;
}

export interface DeleteJoinApplicationRequest {
  /**
   * Id of the JoinApplication to delete.
   * @format GUID
   */
  joinApplicationId: string;
}

export interface DeleteJoinApplicationResponse {}

export interface QueryJoinApplicationsRequest {
  /** WQL expression. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 100
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 1000
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 1000
   */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryJoinApplicationsResponse {
  /** List of JoinApplications. */
  joinApplications?: JoinApplication[];
  /** Paging metadata */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchJoinApplicationsRequest {
  /** WQL expression. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Aggregations are a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * UTC offset or IANA time zone. Valid values are
   * ISO 8601 UTC offsets, such as +02:00 or -06:00,
   * and IANA time zone IDs, such as Europe/Rome.
   *
   * Affects all filters and aggregations returned values.
   * You may override this behavior in a specific filter by providing
   * timestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`.
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not provided. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not provided. */
  to?: number | null;
}

export enum SortType {
  /** Sort by number of matches. */
  COUNT = 'COUNT',
  /** Sort by value of the field alphabetically. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Sort in descending order. */
  DESC = 'DESC',
  /** Sort in ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Count of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
  /**
   * Sum of values.
   * @internal
   */
  SUM = 'SUM',
  /**
   * Average of values.
   * @internal
   */
  AVG = 'AVG',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Whether to sort by number of matches or value of the field. */
  sortType?: SortTypeWithLiterals;
  /** Whether to sort in ascending or descending order. */
  sortDirection?: SortDirectionWithLiterals;
  /** How many aggregations to return. Can be between 1 and 250. 10 is the default. */
  limit?: number | null;
  /** Whether to include or exclude missing values from the aggregation results. Default: `EXCLUDE`. */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets, where during aggregation each entity will be placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Define the operator for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval */
  YEAR = 'YEAR',
  /** Monthly interval */
  MONTH = 'MONTH',
  /** Weekly interval */
  WEEK = 'WEEK',
  /** Daily interval */
  DAY = 'DAY',
  /** Hourly interval */
  HOUR = 'HOUR',
  /** Minute interval */
  MINUTE = 'MINUTE',
  /** Second interval */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Value aggregation. */
  value?: ValueAggregation;
  /** Range aggregation. */
  range?: RangeAggregation;
  /** Scalar aggregation. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value. */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket. */
  RANGE = 'RANGE',
  /** A single-value metric aggregation. For example, min, max, sum, avg. */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.) */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Multi-level aggregation, where each next aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /** Defines how separate search terms in `expression` are combined. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm). */
  fuzzy?: boolean;
}

export enum Mode {
  /** Any of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchJoinApplicationsResponse {
  /** List of JoinApplications. */
  joinApplications?: JoinApplication[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
  /** Aggregation data */
  aggregationData?: AggregationData;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface AggregationData {
  /**
   * key = aggregation name (as derived from search request).
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * User-defined name of aggregation, matches the one provided in request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that matches result. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, matches the one provided in request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in ISO 8601 format.
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form.
 * Aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * User-defined name of aggregation as derived from search request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that must match provided kind as derived from search request. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by as derived from search request.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface BulkCreateJoinApplicationsRequest {
  /**
   * List of JoinApplications to be created
   * @minSize 1
   * @maxSize 100
   */
  joinApplications: JoinApplication[];
  /** set to `true` if you wish to receive back the created JoinApplications in the response */
  returnEntity?: boolean;
}

export interface BulkCreateJoinApplicationsResponse {
  /**
   * List of the bulk create operation results including the JoinApplications and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkJoinApplicationResult[];
  /** Metadata regarding the bulk create operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkJoinApplicationResult {
  /** Metadata regarding the specific single create operation */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: JoinApplication;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpsertJoinApplicationMigrationRequest {
  /**
   * List of join applications to create or update.
   * @minSize 1
   * @maxSize 100
   */
  joinApplications?: JoinApplicationMigration[];
}

export interface JoinApplicationMigration {
  /** JoinApplication to create or update. */
  joinApplication?: JoinApplication;
}

export interface BulkUpsertJoinApplicationMigrationResponse {
  /**
   * Results of the bulk migration operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkJoinApplicationMigrationResult[];
  /** Metadata about the bulk action. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkJoinApplicationMigrationResult {
  /** Metadata regarding the specific single upsert operation. */
  itemMetadata?: ItemMetadata;
  /** Action type of bulk action (INSERT or UPDATE) for that endpoint. */
  action?: BulkActionTypeWithLiterals;
}

export enum BulkActionType {
  INSERT = 'INSERT',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

/** @enumType */
export type BulkActionTypeWithLiterals =
  | BulkActionType
  | 'INSERT'
  | 'UPDATE'
  | 'DELETE';

export interface BulkDeleteJoinApplicationsRequest {
  /**
   * JoinApplication ids to be deleted
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  joinApplicationIds: string[];
}

export interface BulkDeleteJoinApplicationsResponse {
  /**
   * Results
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteJoinApplicationsResponseBulkJoinApplicationResult[];
  /** Metadata regarding the bulk delete operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteJoinApplicationsResponseBulkJoinApplicationResult {
  /** Metadata regarding the specific single delete operation */
  itemMetadata?: ItemMetadata;
}

export interface GetCurrentJoinApplicationRequest {
  /**
   * Program ID to look up the caller's join application for
   * @format GUID
   */
  programId: string;
}

export interface GetCurrentJoinApplicationResponse {
  /** The found JoinApplication (unique per member+program). */
  joinApplication?: JoinApplication;
}

export interface MyJoinApplicationRequest {
  /**
   * Program ID to look up the caller's join application for
   * @format GUID
   */
  programId: string;
}

export interface MyJoinApplicationResponse {
  /** The found JoinApplication (unique per member+program). */
  joinApplication?: JoinApplication;
}

export interface ApproveJoinApplicationRequest {
  /**
   * ID of the JoinApplication to approve
   * @format GUID
   */
  joinApplicationId: string;
}

export interface ApproveJoinApplicationResponse {
  /** The approved JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface DeclineJoinApplicationRequest {
  /**
   * ID of the JoinApplication to decline
   * @format GUID
   */
  joinApplicationId: string;
}

export interface DeclineJoinApplicationResponse {
  /** The declined JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface CancelJoinApplicationRequest {
  /**
   * ID of the JoinApplication to cancel
   * @format GUID
   */
  joinApplicationId: string;
}

export interface CancelJoinApplicationResponse {
  /** The cancelled JoinApplication with updated status */
  joinApplication?: JoinApplication;
}

export interface AcceptInviteRequest {
  /**
   * ID of the JoinApplication to accept
   * @format GUID
   */
  joinApplicationId: string;
}

export interface AcceptInviteResponse {
  /** The JoinApplication with status updated from INVITED to APPROVED */
  joinApplication?: JoinApplication;
}

export interface BulkUpdateJoinApplicationTagsRequest {
  /**
   * JoinApplication IDs to update tags for (1-100 items)
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  joinApplicationIds: string[];
  /** Tags to assign to the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

export interface BulkUpdateJoinApplicationTagsResponse {
  /**
   * Results for each JoinApplication
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateJoinApplicationTagsResult[];
  /** Metadata about the bulk operation */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateJoinApplicationTagsResult {
  /** Metadata for this specific item */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateJoinApplicationTagsByFilterRequest {
  /** WQL filter to select JoinApplications */
  filter: Record<string, any> | null;
  /** Tags to assign to matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  assignTags?: Tags;
  /** Tags to unassign from matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified. */
  unassignTags?: Tags;
}

export interface BulkUpdateJoinApplicationTagsByFilterResponse {
  /**
   * Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @format GUID
   */
  jobId?: string;
}

export interface PreparePaymentRequest {
  /**
   * ID of the JoinApplication to prepare payment for
   * @format GUID
   */
  joinApplicationId: string;
  /** Type of payment to prepare */
  paymentType: PaymentTypeWithLiterals;
}

export enum PaymentType {
  /** One-time payment through Wix Payments. */
  SINGLE_PAYMENT = 'SINGLE_PAYMENT',
  /** Payment through Wix Pricing Plans. */
  PAID_PLANS = 'PAID_PLANS',
}

/** @enumType */
export type PaymentTypeWithLiterals =
  | PaymentType
  | 'SINGLE_PAYMENT'
  | 'PAID_PLANS';

export interface PreparePaymentResponse {
  /** JoinApplication with payment info refreshed and payment status set to PAYMENT_IN_PROGRESS. */
  joinApplication?: JoinApplication;
}

export interface ApplyCouponRequest {
  /**
   * ID of the JoinApplication to apply the coupon to
   * @format GUID
   */
  joinApplicationId: string;
  /**
   * Coupon code to apply
   * @maxLength 100
   */
  couponCode: string;
}

export interface ApplyCouponResponse {
  /**
   * Coupon ID that was applied
   * @format GUID
   */
  couponId?: string;
  /**
   * Original order subtotal before discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  subTotal?: string;
  /**
   * Discount amount applied, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  discount?: string;
  /**
   * Final total after discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  total?: string;
}

export interface RemoveCouponRequest {
  /**
   * ID of the JoinApplication to remove the coupon from
   * @format GUID
   */
  joinApplicationId: string;
  /**
   * ID of the coupon to remove (must match the coupon applied to the order)
   * @format GUID
   */
  couponId: string;
}

export interface RemoveCouponResponse {
  /**
   * Final total after coupon removal, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.
   * @maxLength 50
   */
  total?: string;
}

export interface CompleteFreeCouponPaymentRequest {
  /**
   * ID of the JoinApplication to complete free coupon payment for
   * @format GUID
   */
  joinApplicationId: string;
}

export interface CompleteFreeCouponPaymentResponse {
  /** The JoinApplication with payment status updated to PAYMENT_SUCCESSFUL */
  joinApplication?: JoinApplication;
}

export interface BulkInviteMembersToProgramByFilterRequest {
  /**
   * Program to which bulk invite members.
   * @format GUID
   */
  programId: string;
  /**
   * Exclude site members.
   * @format GUID
   * @maxSize 100
   */
  excludeMemberIds?: string[];
}

export interface BulkInviteMembersToProgramByFilterResponse {
  /**
   * Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @format GUID
   */
  jobId?: string;
}

export interface DeleteAllJoinApplicationsMigrationRequest {}

export interface DeleteAllJoinApplicationsMigrationResponse {}

export interface ReindexJoinApplicationsSearchRequest {
  /**
   * @minSize 1
   * @maxSize 1000
   * @format GUID
   */
  joinApplicationIds?: string[];
}

export interface ReindexJoinApplicationsSearchResponse {}

export interface SyncPaymentExpirationTasksMigrationRequest {}

export interface SyncPaymentExpirationTasksMigrationResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

/** This webhook is triggered whenever a payment transaction is updated (including refunds) via specific [Wix Apps](https://dev.wix.com/api/rest/wix-payments/about-wix-payments). */
export interface PaymentEvent extends PaymentEventEventOneOf {
  /** `TRANSACTION_STATUS_CHANGED` event details. */
  transactionStatusChangedEvent?: TransactionStatusChangedEvent;
  /** `REFUND_STATUS_CHANGED` event details. */
  refundStatusChangedEvent?: RefundStatusChangedEvent;
  /**
   * `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @deprecated `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @targetRemovalDate 2026-06-30
   */
  recurringPaymentStatusChangedEvent?: RecurringPaymentStatusChangedEvent;
  /** `TRANSACTION_UPDATED` event details. */
  transactionUpdatedEvent?: TransactionUpdatedEvent;
  /** `TRANSACTION_CREATED` event details. */
  transactionCreatedEvent?: TransactionCreatedEvent;
  /**
   * Unique event ID.
   * @format GUID
   */
  id?: string;
  /** Event timestamp */
  eventTime?: Date | null;
  /**
   * ID of the [Wix app](https://dev.wix.com/docs/rest/articles/getting-started/wix-business-solutions) that triggered this event.
   * @maxLength 100
   */
  wixAppId?: string;
  /**
   * Unique ID assigned to each Wix app in each site.
   * @format GUID
   */
  wixAppInstanceId?: string;
  /** Event type. */
  eventType?: EventTypeWithLiterals;
  /**
   * ID of Wix app that mediated between Wix Payments and the Wix app that triggered the event (if relevant).
   * @maxLength 100
   */
  managingWixAppId?: string | null;
}

/** @oneof */
export interface PaymentEventEventOneOf {
  /** `TRANSACTION_STATUS_CHANGED` event details. */
  transactionStatusChangedEvent?: TransactionStatusChangedEvent;
  /** `REFUND_STATUS_CHANGED` event details. */
  refundStatusChangedEvent?: RefundStatusChangedEvent;
  /**
   * `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @deprecated `RECURRING_PAYMENT_STATUS_CHANGED` event details.
   * @targetRemovalDate 2026-06-30
   */
  recurringPaymentStatusChangedEvent?: RecurringPaymentStatusChangedEvent;
  /** `TRANSACTION_UPDATED` event details. */
  transactionUpdatedEvent?: TransactionUpdatedEvent;
  /** `TRANSACTION_CREATED` event details. */
  transactionCreatedEvent?: TransactionCreatedEvent;
}

export enum EventType {
  /** Fired when a transaction's status changes. */
  TRANSACTION_STATUS_CHANGED = 'TRANSACTION_STATUS_CHANGED',
  /** Fired when a refund's status changes. */
  REFUND_STATUS_CHANGED = 'REFUND_STATUS_CHANGED',
  /** Fired when a legacy subscription's status changes. */
  RECURRING_PAYMENT_STATUS_CHANGED = 'RECURRING_PAYMENT_STATUS_CHANGED',
  /** Fired when a transaction is updated. */
  TRANSACTION_UPDATED = 'TRANSACTION_UPDATED',
  /** Fired when a transaction is created. */
  TRANSACTION_CREATED = 'TRANSACTION_CREATED',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'TRANSACTION_STATUS_CHANGED'
  | 'REFUND_STATUS_CHANGED'
  | 'RECURRING_PAYMENT_STATUS_CHANGED'
  | 'TRANSACTION_UPDATED'
  | 'TRANSACTION_CREATED';

export interface TransactionStatusChangedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** New transaction details. */
  transaction?: Transaction;
  /** Previous transaction status. */
  previousStatus?: TransactionStatusWithLiterals;
}

export interface Order {
  id?: string;
  /** Wix app order ID */
  wixAppOrderId?: string;
  /** Wix app buyer ID */
  wixAppBuyerId?: string | null;
  /** Items included in the order */
  items?: OrderItem[];
  /**
   * total number of items in order
   * items_total_count > `items`.size if `items` doesn't contain all items
   */
  itemsTotalCount?: number;
  /** Additional charges to the order - e.g., tax, shipping or discount (optional) */
  additionalCharges?: OrderAdditionalCharges;
  /** Customer's shipping address (optional) */
  shippingAddress?: Address;
  /** Customer's shipping address details (optional) */
  shippingAddressContacts?: FullAddressContactDetails;
  /** Order creation date */
  createdAt?: Date | null;
  /** External data which is stored with the order */
  externalData?: Record<string, string>;
  /**
   * Vertical invoice ID
   * @maxLength 200
   */
  verticalInvoiceId?: string | null;
  /** the part of total amount taken by platform from the amount sent to merchant */
  platformFee?: number | null;
  /**
   * ID of the buyer's contact record in the contacts system.
   * @format GUID
   */
  contactId?: string | null;
  /** Indicates that this order is a test order. Test orders are simulated and do not move real money. */
  test?: boolean | null;
}

/** Item included in order */
export interface OrderItem {
  /** Order item ID (required) */
  id?: string;
  /** Order item name (required) */
  name?: string;
  /** Quantity (required) */
  quantity?: number;
  /** Total price charged for this item in the order - quantity * price (required) */
  price?: number;
  /** Order item description */
  description?: string;
  /** Weight of one item */
  weightInKg?: number;
  /** Whether the item is a physical a digital product */
  category?: OrderItemCategoryWithLiterals;
  /**
   * UN/CEFACT unit of measure code for the order item, following the UNECE Recommendation 20 standard (e.g., "EA", "KG")
   * @minLength 2
   * @maxLength 3
   */
  unitOfMeasureCode?: string | null;
  /**
   * UNSPSC commodity code classifying the type of product or service
   * @minLength 8
   * @maxLength 8
   */
  commodityCode?: string | null;
  /**
   * Merchant-defined product code or SKU identifier for the order item
   * @minLength 1
   * @maxLength 127
   */
  productCode?: string | null;
  /** Per-item tax amount */
  tax?: number | null;
  /** Per-item discount amount */
  discount?: number | null;
  /** Whether the item is a donation. When true, PayPal's category will be emitted as "DONATION". */
  donation?: boolean;
}

/** Order item type */
export enum OrderItemCategory {
  UNDEFINED = 'UNDEFINED',
  /** Physical product */
  PHYSICAL = 'PHYSICAL',
  /** Digital product */
  DIGITAL = 'DIGITAL',
}

/** @enumType */
export type OrderItemCategoryWithLiterals =
  | OrderItemCategory
  | 'UNDEFINED'
  | 'PHYSICAL'
  | 'DIGITAL';

/** Describing any additional charges in the order. They are not required, but can't be added later. */
export interface OrderAdditionalCharges {
  /** Absolute taxes charged to the order */
  tax?: number;
  /** Shipping cost charged to the order */
  shipping?: number;
  /** Discount amount applied to the order */
  discount?: number;
  /**
   * Application fee
   * @deprecated Application fee
   * @replacedBy order.platform_fee
   * @targetRemovalDate 2025-12-01
   */
  applicationFee?: number;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /**
   * Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2), e.g. US-NY
   * @internal
   * @maxLength 20
   */
  subdivisionIso31662?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formattedAddress?: string | null;
  /**
   * Free text to help find the address.
   * @internal
   */
  hint?: string | null;
  /**
   * Coordinates of the physical address.
   * @internal
   */
  geocode?: AddressLocation;
  /**
   * Country full name.
   * @internal
   */
  countryFullname?: string | null;
  /**
   * Subdivision full name.
   * @internal
   */
  subdivisionFullname?: string | null;
  /**
   * Multi-level subdivisions from top to bottom.
   * @internal
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /**
   * Apartment number.
   * @internal
   */
  apt?: string;
  /**
   * Optional address line 1
   * @internal
   */
  formattedAddressLine?: string | null;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
  /**
   * Subdivision level
   * @internal
   */
  type?: SubdivisionTypeWithLiterals;
  /**
   * Free text description of subdivision type.
   * @internal
   */
  typeInfo?: string | null;
  /**
   * Standard organizations details (e.g. ISO).
   * @internal
   */
  standardDetails?: StandardDetails;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

/** Full contact details for an address */
export interface FullAddressContactDetails {
  /** Contact's first name. */
  firstName?: string | null;
  /** Contact's last name. */
  lastName?: string | null;
  /**
   * Contact's full name.
   * @internal
   */
  fullName?: string | null;
  /**
   * Contact's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /** Contact's company name. */
  company?: string | null;
  /**
   * Email associated with the address.
   * @format EMAIL
   */
  email?: string | null;
  /** Tax info. Currently usable only in Brazil. */
  vatId?: VatId;
}

export interface VatId {
  /** Customer's tax ID. */
  id?: string;
  /**
   * Tax type.
   *
   * Supported values:
   * + `CPF`: for individual tax payers
   * + `CNPJ`: for corporations
   */
  type?: VatTypeWithLiterals;
}

/** tax info types */
export enum VatType {
  UNSPECIFIED = 'UNSPECIFIED',
  /** CPF - for individual tax payers. */
  CPF = 'CPF',
  /** CNPJ - for corporations */
  CNPJ = 'CNPJ',
}

/** @enumType */
export type VatTypeWithLiterals = VatType | 'UNSPECIFIED' | 'CPF' | 'CNPJ';

export interface Transaction extends TransactionPaymentMethodDataOneOf {
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
  /** Transaction ID */
  id?: string;
  /** Provider transaction ID */
  providerTransactionId?: string | null;
  /** Amount and currency of transaction */
  amount?: CurrencyAmount;
  /** Refunded amount and currency of transaction */
  refundedAmount?: CurrencyAmount;
  /** Updated transaction status */
  status?: TransactionStatusWithLiterals;
  /** Reason provided for status change */
  reasonCode?: string | null;
  /** Payment method */
  paymentMethod?: string;
  /** Payment service provider */
  paymentProvider?: string;
  /** Customer's billing address (optional) */
  billingAddress?: Address;
  /** Customer's billing address - contact details (optional) */
  billingAddressContacts?: FullAddressContactDetails;
  /** Details of subscription, which (if) the transaction is a part of */
  recurringPaymentDetails?: RecurringPaymentDetails;
  /** The details of a payment method account. */
  paymentMethodAccountInfo?: V3AccountInfo;
  /** Transaction creation date */
  createdAt?: Date | null;
  /** Total number of installments */
  installments?: number;
  /** Scheduled action for this transaction */
  scheduledAction?: ScheduledAction;
  /**
   * order id associated with the transaction on the vertical side
   * @minLength 1
   * @maxLength 100
   */
  verticalOrderId?: string | null;
  appliedPlatformFee?: number | null;
  /**
   * Whether the transaction is from a sandbox (test) environment or production (live) environment.
   * @readonly
   */
  sandbox?: boolean | null;
  /**
   * ID correlating all attempts of one logical charge within a failover chain. Internal use only.
   * @internal
   * @format GUID
   * @readonly
   */
  failoverChainId?: string | null;
  /**
   * Number of failovers that preceded this attempt within its failover chain. 0 = the original attempt (no failover occurred); N = this transaction is the result of the Nth failover. Internal use only.
   * @internal
   * @max 2
   * @readonly
   */
  failoverCount?: number | null;
}

/** @oneof */
export interface TransactionPaymentMethodDataOneOf {
  creditCardPaymentMethodData?: CreditCardPaymentMethodData;
}

export interface CurrencyAmount {
  /** Amount */
  amount?: number;
  currency?: string;
}

export enum TransactionStatus {
  UNDEFINED = 'UNDEFINED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  PENDING_MERCHANT = 'PENDING_MERCHANT',
  CANCELED = 'CANCELED',
  DECLINED = 'DECLINED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  AUTHORIZED = 'AUTHORIZED',
  VOIDED = 'VOIDED',
}

/** @enumType */
export type TransactionStatusWithLiterals =
  | TransactionStatus
  | 'UNDEFINED'
  | 'APPROVED'
  | 'PENDING'
  | 'PENDING_MERCHANT'
  | 'CANCELED'
  | 'DECLINED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | 'AUTHORIZED'
  | 'VOIDED';

export interface RecurringPaymentDetails {
  /** Current cycle number (Recurring payments only) */
  cycleNumber?: number;
}

export interface CreditCardPaymentMethodData {
  /** Card issuing network */
  network?: string;
  /** Card number with only bin and last4 positions showed (non-PCI data) */
  maskedCreditCard?: string;
  /** Card holder's full name specified on the card. */
  holderName?: string;
  /** Card expiration month. */
  expiryMonth?: string;
  /**
   * Card expiration year.
   * @minLength 4
   * @maxLength 4
   */
  expiryYear?: string | null;
  /**
   * Card last 4 digits.
   * @minLength 4
   * @maxLength 4
   */
  lastFourDigits?: string | null;
  /**
   * Card BIN (Bank Identification Number). It's the first 4-8 digits of a card number.
   * @minLength 4
   * @maxLength 8
   */
  bin?: string | null;
}

export interface V3AccountInfo {
  /**
   * The email of an Account used by Payment Method
   * @format EMAIL
   */
  email?: string | null;
}

export interface ScheduledAction {
  /** type of the action */
  type?: ScheduledActionTypeWithLiterals;
  /** the date and time of the action */
  executionDate?: Date | null;
}

export enum ScheduledActionType {
  VOID = 'VOID',
  CAPTURE = 'CAPTURE',
}

/** @enumType */
export type ScheduledActionTypeWithLiterals =
  | ScheduledActionType
  | 'VOID'
  | 'CAPTURE';

export interface RefundStatusChangedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
  /** Updated refund details. */
  refund?: Refund;
  /** Previous refund status. */
  previousStatus?: RefundStatusWithLiterals;
}

export interface Refund {
  id?: string;
  /** Amount and currency of refund */
  amount?: CurrencyAmount;
  /** Updated refund status */
  status?: RefundStatusWithLiterals;
  /** Reason provided for status change */
  reasonCode?: string | null;
  /** Refund creation date */
  createdAt?: Date | null;
}

export enum RefundStatus {
  UNDEFINED = 'UNDEFINED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
}

/** @enumType */
export type RefundStatusWithLiterals =
  | RefundStatus
  | 'UNDEFINED'
  | 'APPROVED'
  | 'PENDING'
  | 'DECLINED';

export interface RecurringPaymentStatusChangedEvent {
  /** Order details(limited to 30 items). */
  order?: Order;
  /** Recurring payment status. */
  status?: RecurringPaymentStatusStatusWithLiterals;
  /** Previous recurring payment status. */
  previousStatus?: RecurringPaymentStatusStatusWithLiterals;
  /** Details of RecurringPayment cancellation (empty if `status` is not CANCELLED). */
  cancellationDetails?: RecurringPaymentCancellationDetails;
}

export enum RecurringPaymentStatusStatus {
  UNDEFINED = 'UNDEFINED',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  FINISHED = 'FINISHED',
  SUSPENDED = 'SUSPENDED',
}

/** @enumType */
export type RecurringPaymentStatusStatusWithLiterals =
  | RecurringPaymentStatusStatus
  | 'UNDEFINED'
  | 'PENDING'
  | 'ACTIVE'
  | 'CANCELLED'
  | 'FINISHED'
  | 'SUSPENDED';

export interface RecurringPaymentCancellationDetails {
  /** Initiator of cancellation */
  initiator?: InitiatorWithLiterals;
  /** Successfully paid recurring cycles count before the cancellation */
  paidRegularCycleCount?: number;
}

export enum Initiator {
  UNDEFINED = 'UNDEFINED',
  BUYER = 'BUYER',
  MERCHANT = 'MERCHANT',
  PAYMENT = 'PAYMENT',
  SETUP = 'SETUP',
}

/** @enumType */
export type InitiatorWithLiterals =
  | Initiator
  | 'UNDEFINED'
  | 'BUYER'
  | 'MERCHANT'
  | 'PAYMENT'
  | 'SETUP';

export interface TransactionUpdatedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
  /** Is event triggered by request to clean user data */
  triggeredByAnonymizeRequest?: boolean | null;
}

export interface TransactionCreatedEvent {
  /** Order details (limited to 30 items). */
  order?: Order;
  /** Updated transaction details. */
  transaction?: Transaction;
}

export interface BenefitNotification {
  /**
   * Plan unique ID
   * @format GUID
   */
  planId?: string;
  /**
   * App def ID
   * @format GUID
   */
  appDefId?: string;
  /** Current benefit details */
  benefit?: Benefit;
  /** Previous benefit */
  prevBenefit?: Benefit;
  /** Notification event */
  event?: EventWithLiterals;
}

export interface Benefit {
  /**
   * Benefit unique ID
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /** Benefit Type */
  benefitType?: BenefitTypeWithLiterals;
  /**
   * Resource IDs that serves by this benefit
   * @format GUID
   */
  resourceIds?: string[];
  /** Amount of credits that provided by this benefit */
  creditAmount?: number | null;
  /**
   * additional details related to benefit; limited to 20 entries, 20 symbols for key and 20 symbols for value
   * @maxSize 20
   */
  customFields?: Record<string, string>;
  /** return value only in case it required in the ListRequest, true means that benefit's type could be updated */
  editable?: boolean | null;
  /** Benefit behavior */
  behavior?: Behavior;
  /**
   * Id of the app associated with this benefit
   * @format GUID
   * @readonly
   */
  appDefId?: string | null;
}

export interface EntryPass {}

export interface Discount extends DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

/** @oneof */
export interface DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

export enum BenefitType {
  /** Should never be used */
  UNDEFINED = 'UNDEFINED',
  /** Limited benefit type */
  LIMITED = 'LIMITED',
  /** Unlimited benefit type */
  UNLIMITED = 'UNLIMITED',
}

/** @enumType */
export type BenefitTypeWithLiterals =
  | BenefitType
  | 'UNDEFINED'
  | 'LIMITED'
  | 'UNLIMITED';

export interface Behavior extends BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

/** @oneof */
export interface BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

export enum Event {
  Updated = 'Updated',
  Deleted = 'Deleted',
  Created = 'Created',
}

/** @enumType */
export type EventWithLiterals = Event | 'Updated' | 'Deleted' | 'Created';

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateJoinApplicationApplicationErrors =
  | {
      code?: 'PROGRAM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PROGRAM_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MISSING_MEMBER_ID';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkCreateJoinApplicationsApplicationErrors =
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GetCurrentJoinApplicationApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type MyJoinApplicationApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type ApproveJoinApplicationApplicationErrors =
  | {
      code?: 'INVALID_JOIN_APPLICATION_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DeclineJoinApplicationApplicationErrors = {
  code?: 'INVALID_JOIN_APPLICATION_STATUS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CancelJoinApplicationApplicationErrors = {
  code?: 'INVALID_JOIN_APPLICATION_STATUS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type AcceptInviteApplicationErrors =
  | {
      code?: 'INVALID_JOIN_APPLICATION_STATUS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PARTICIPANTS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateJoinApplicationTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateJoinApplicationTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type PreparePaymentApplicationErrors =
  | {
      code?: 'OP_APP_NOT_INSTALLED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PP_APP_NOT_INSTALLED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NO_PAID_PLAN_CONNECTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ApplyCouponApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_IS_ALREADY_APPLIED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type RemoveCouponApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CompleteFreeCouponPaymentApplicationErrors =
  | {
      code?: 'MISSING_PAYMENT_ORDER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NO_COUPON_APPLIED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COUPON_DOES_NOT_COVER_FULL_AMOUNT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_PAYMENT_STATUS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkInviteMembersToProgramByFilterApplicationErrors =
  | {
      code?: 'PROGRAM_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PROGRAM_NOT_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PREMIUM_PLAN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    };
